/**
 * Scenarios API Service
 *
 * This service provides methods for scenario management operations.
 * It handles updating scenarios with automatic version tracking for
 * the admin scenario management page.
 *
 * The service is designed to easily switch from mock data to real API calls.
 * Set the REACT_APP_API_BASE_URL environment variable to connect to the real API.
 *
 * Note: Future integration with Bedrock Prompt Management is planned for
 * enhanced versioning capabilities.
 *
 * @module services/scenarios
 */

import { Scenario, ScenarioUpdate } from '../types/scenario';
import { getIdToken } from './auth';

/**
 * API configuration from environment variables.
 * Falls back to empty string for mock mode when not configured.
 */
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Flag to determine if we should use mock data.
 * When API_BASE_URL is not set, the service operates in mock mode.
 */
const USE_MOCK_DATA = !API_BASE_URL;

/**
 * Error codes for scenarios-related errors.
 */
export type ScenariosErrorCode =
  | 'FETCH_FAILED'
  | 'UPDATE_FAILED'
  | 'UNAUTHORIZED'
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  | 'VALIDATION_ERROR'
  | 'VERSION_CONFLICT'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

/**
 * Custom error class for scenarios-related errors.
 * Provides structured error information for better error handling.
 */
export class ScenariosError extends Error {
  /** HTTP status code if applicable */
  statusCode: number;
  /** Error code for programmatic error handling */
  code: ScenariosErrorCode;
  /** Original error if this error wraps another */
  originalError?: Error;

  constructor(
    message: string,
    statusCode: number,
    code: ScenariosErrorCode = 'UNKNOWN',
    originalError?: Error
  ) {
    super(message);
    this.name = 'ScenariosError';
    this.statusCode = statusCode;
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * Mock scenario data for development and testing.
 * This data is used when the API is not configured.
 * Note: This is a mutable copy that gets updated during mock operations.
 */
let MOCK_SCENARIOS: Scenario[] = [
  {
    id: 'scenario-1',
    name: 'Enterprise Software Sales Call',
    role: 'Sales Representative',
    industry: 'Technology',
    description: 'Practice selling enterprise software solutions to IT decision makers',
    status: 'active',
    version: 1,
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'scenario-2',
    name: 'Patient Consultation',
    role: 'Healthcare Provider',
    industry: 'Healthcare and Life Sciences',
    description: 'Practice patient communication and consultation skills',
    status: 'active',
    version: 1,
    updatedAt: '2024-01-02T00:00:00Z',
  },
  {
    id: 'scenario-3',
    name: 'Investment Advisory Meeting',
    role: 'Financial Advisor',
    industry: 'Financial Services',
    description: 'Practice discussing investment strategies with clients',
    status: 'active',
    version: 2,
    updatedAt: '2024-01-10T00:00:00Z',
  },
  {
    id: 'scenario-4',
    name: 'Customer Support Escalation',
    role: 'Support Specialist',
    industry: 'Technology',
    description: 'Handle escalated customer complaints and technical issues',
    status: 'active',
    version: 1,
    updatedAt: '2024-01-03T00:00:00Z',
  },
  {
    id: 'scenario-5',
    name: 'Insurance Claim Discussion',
    role: 'Claims Adjuster',
    industry: 'Financial Services',
    description: 'Practice explaining claim decisions to policyholders',
    status: 'inactive',
    version: 3,
    updatedAt: '2024-01-12T00:00:00Z',
  },
  {
    id: 'scenario-6',
    name: 'Retail Banking Consultation',
    role: 'Bank Representative',
    industry: 'Financial Services',
    description: 'Assist customers with banking products and services',
    status: 'active',
    version: 1,
    updatedAt: '2024-01-04T00:00:00Z',
  },
];

/**
 * Helper function to make authenticated API requests.
 *
 * @param endpoint - The API endpoint (relative to API_BASE_URL)
 * @param options - Optional fetch options
 * @returns Promise resolving to the parsed JSON response
 * @throws ScenariosError if the request fails
 */
async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const token = await getIdToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorMessage = await response.text().catch(() => 'Unknown error');
      const code = getErrorCode(response.status);
      throw new ScenariosError(
        errorMessage || `Request failed with status ${response.status}`,
        response.status,
        code
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof ScenariosError) {
      throw error;
    }
    // Handle network errors
    throw new ScenariosError(
      'Network error occurred while updating scenario',
      0,
      'NETWORK_ERROR',
      error instanceof Error ? error : undefined
    );
  }
}

/**
 * Map HTTP status codes to error codes.
 *
 * @param statusCode - HTTP status code
 * @returns Corresponding ScenariosErrorCode
 */
function getErrorCode(statusCode: number): ScenariosErrorCode {
  switch (statusCode) {
    case 400:
      return 'VALIDATION_ERROR';
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'VERSION_CONFLICT';
    default:
      return 'UPDATE_FAILED';
  }
}

/**
 * Simulate network delay for mock data.
 * This helps test loading states in the UI.
 *
 * @param ms - Delay in milliseconds
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Update a scenario with automatic version tracking.
 *
 * Updates the specified scenario with the provided changes and automatically
 * increments the version number. This supports scenario versioning as required
 * for tracking changes over time.
 *
 * The version number is automatically incremented on each successful update.
 * The updatedAt timestamp is also automatically set to the current time.
 *
 * This endpoint requires admin privileges.
 *
 * In mock mode, updates the mock data after a simulated delay.
 *
 * Note: Future integration with Bedrock Prompt Management is planned for
 * enhanced versioning capabilities including version history and rollback.
 *
 * @param scenarioId - The ID of the scenario to update
 * @param updates - Partial scenario object containing fields to update
 * @returns Promise resolving to the updated Scenario with new version
 * @throws ScenariosError if the request fails, scenario not found, or lacks admin privileges
 *
 * @example
 * ```typescript
 * try {
 *   // Update scenario name and description
 *   const updated = await updateScenario('scenario-123', {
 *     name: 'Updated Scenario Name',
 *     description: 'New description for the scenario',
 *   });
 *   console.log('Scenario updated to version:', updated.version);
 *
 *   // Deactivate a scenario
 *   const deactivated = await updateScenario('scenario-123', {
 *     status: 'inactive',
 *   });
 *   console.log('Scenario deactivated');
 * } catch (error) {
 *   if (error instanceof ScenariosError) {
 *     if (error.code === 'NOT_FOUND') {
 *       console.error('Scenario not found');
 *     } else if (error.code === 'VERSION_CONFLICT') {
 *       console.error('Scenario was modified by another user');
 *     } else {
 *       console.error('Failed to update scenario:', error.message);
 *     }
 *   }
 * }
 * ```
 *
 * @see {@link Scenario} for the full scenario interface
 * @see {@link ScenarioUpdate} for the update payload type
 *
 * **Validates: Requirements 12.4**
 */
export async function updateScenario(
  scenarioId: string,
  updates: ScenarioUpdate
): Promise<Scenario> {
  if (USE_MOCK_DATA) {
    // Simulate network delay for realistic loading state testing
    await delay(400);

    // Find the scenario in mock data
    const scenarioIndex = MOCK_SCENARIOS.findIndex(
      (scenario) => scenario.id === scenarioId
    );

    if (scenarioIndex === -1) {
      throw new ScenariosError(
        `Scenario with ID ${scenarioId} not found`,
        404,
        'NOT_FOUND'
      );
    }

    const existingScenario = MOCK_SCENARIOS[scenarioIndex];

    // Create updated scenario with incremented version
    const updatedScenario: Scenario = {
      ...existingScenario,
      ...updates,
      id: existingScenario.id, // Ensure ID cannot be changed
      version: (existingScenario.version || 1) + 1,
      updatedAt: new Date().toISOString(),
    };

    // Update the mock data
    MOCK_SCENARIOS = MOCK_SCENARIOS.map((scenario) =>
      scenario.id === scenarioId ? updatedScenario : scenario
    );

    return updatedScenario;
  }

  // Real API call with versioning
  return fetchWithAuth<Scenario>(`/admin/scenarios/${scenarioId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Fetch all available scenarios.
 *
 * Returns a list of all scenarios for the admin management page.
 * In mock mode, returns predefined mock data after a simulated delay.
 *
 * @returns Promise resolving to an array of Scenario objects
 * @throws ScenariosError if the request fails
 *
 * @example
 * ```typescript
 * try {
 *   const scenarios = await getScenarios();
 *   console.log('Available scenarios:', scenarios.length);
 * } catch (error) {
 *   if (error instanceof ScenariosError) {
 *     console.error('Failed to fetch scenarios:', error.message);
 *   }
 * }
 * ```
 */
export async function getScenarios(): Promise<Scenario[]> {
  if (USE_MOCK_DATA) {
    await delay(300);
    return [...MOCK_SCENARIOS];
  }

  return fetchWithAuth<Scenario[]>('/scenarios');
}

/**
 * Get a scenario by ID.
 *
 * Retrieves a single scenario by its ID. Useful for fetching the latest
 * version of a scenario after an update or for displaying scenario details.
 *
 * In mock mode, returns the scenario from mock data after a simulated delay.
 *
 * @param scenarioId - The ID of the scenario to retrieve
 * @returns Promise resolving to the Scenario object
 * @throws ScenariosError if the scenario is not found or request fails
 *
 * @example
 * ```typescript
 * try {
 *   const scenario = await getScenarioById('scenario-123');
 *   console.log('Scenario:', scenario.name, 'Version:', scenario.version);
 * } catch (error) {
 *   if (error instanceof ScenariosError && error.code === 'NOT_FOUND') {
 *     console.error('Scenario not found');
 *   }
 * }
 * ```
 */
export async function getScenarioById(scenarioId: string): Promise<Scenario> {
  if (USE_MOCK_DATA) {
    // Simulate network delay for realistic loading state testing
    await delay(200);

    const scenario = MOCK_SCENARIOS.find((s) => s.id === scenarioId);

    if (!scenario) {
      throw new ScenariosError(
        `Scenario with ID ${scenarioId} not found`,
        404,
        'NOT_FOUND'
      );
    }

    return { ...scenario };
  }

  return fetchWithAuth<Scenario>(`/admin/scenarios/${scenarioId}`);
}

/**
 * Scenarios service object for convenient access to all scenario management functions.
 * Provides a unified interface for scenario administration operations.
 */
export const scenariosService = {
  getScenarios,
  updateScenario,
  getScenarioById,
};

export default scenariosService;
